import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, FileText, Sparkles, User, Copy, Gift, Wallet, Download } from "lucide-react";
import { toast } from "sonner";
import { getMyProfile, updateMyProfile } from "@/lib/user.functions";
import { getMyReferralStats } from "@/lib/referrals.functions";
import { createPortalSession, getStripeData } from "@/utils/payments.functions";
import { planLabel, type PlanTier } from "@/hooks/use-my-profile";
import { getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";
import { useAccountBalance, setAccountBalance } from "@/lib/account-balance";
import { exportMyData } from "@/lib/account-export.functions";

export const Route = createFileRoute("/_authenticated/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const fetchProfile = useServerFn(getMyProfile);
  const update = useServerFn(updateMyProfile);
  const fetchReferrals = useServerFn(getMyReferralStats);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["my-profile"], queryFn: () => fetchProfile() });
  const { data: refStats } = useQuery({
    queryKey: ["my-referrals"],
    queryFn: () => fetchReferrals(),
    staleTime: 30_000,
  });
  const [fullName, setFullName] = useState("");
  const [saving, setSaving] = useState(false);

  const referralLink =
    refStats?.referralCode && typeof window !== "undefined"
      ? `${window.location.origin}/?ref=${refStats.referralCode}`
      : "";
  const copyReferral = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const profile = data?.profile;
  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

  const saveName = async () => {
    if (!fullName.trim()) return;
    setSaving(true);
    try {
      await update({ data: { full_name: fullName.trim() } });
      qc.invalidateQueries({ queryKey: ["my-profile"] });
      toast.success("Profile updated");
      setFullName("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const portal = useServerFn(createPortalSession);
  const fetchStripeData = useServerFn(getStripeData);
  const paymentsReady = hasPaymentsConfigured();
  const stripeEnv = paymentsReady
    ? (() => {
        try {
          return getStripeEnvironment();
        } catch {
          return null;
        }
      })()
    : null;

  const { data: billing, isLoading: billingLoading } = useQuery({
    queryKey: ["stripe-data", stripeEnv],
    queryFn: () => fetchStripeData({ data: { environment: stripeEnv! } }),
    enabled: !!stripeEnv,
    staleTime: 30_000,
  });

  const activeSub =
    billing && !("error" in billing)
      ? billing.subscriptions.find((s) =>
          ["active", "trialing", "past_due"].includes(s.status),
        ) ??
        billing.subscriptions.find(
          (s) => s.status === "canceled" && s.current_period_end && new Date(s.current_period_end) > new Date(),
        ) ??
        null
      : null;

  const invoices =
    billing && !("error" in billing) ? billing.invoices.slice(0, 6) : [];

  const openPortal = async () => {
    if (!stripeEnv) return;
    try {
      const result = await portal({
        data: { environment: stripeEnv, returnUrl: window.location.href },
      });
      if ("error" in result) throw new Error(result.error);
      window.open(result.url, "_blank");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open billing portal");
    }
  };

  return (
    <main className="flex-1 p-4 lg:p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <Card className="border-border/60 bg-card/60 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
              <User className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h1 className="font-display text-2xl font-bold">
                {profile?.full_name || profile?.display_name || "Trader"}
              </h1>
              <p className="text-sm text-muted-foreground">{profile?.email}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-md border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
                  {planLabel(profile?.plan as PlanTier | undefined)} Plan
                </span>
                <span className="rounded-md border border-border bg-card/60 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                  Member since {memberSince}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Stat label="Total signals received" value={data?.signalsCount?.toString() ?? "0"} />
            <Stat label="Account status" value={profile?.banned ? "Suspended" : "Active"} />
          </div>
        </Card>

        <Card className="border-border/60 bg-card/60 p-6">
          <h2 className="font-display text-lg font-bold">Edit profile</h2>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                Full name
              </Label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={profile?.full_name || profile?.display_name || "Your name"}
                className="bg-input/50 font-mono"
              />
            </div>
            <Button onClick={saveName} disabled={saving || !fullName.trim()}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </Card>

        <Card className="border-border/60 bg-card/60 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold">Billing</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage your subscription, payment method and invoices.
              </p>
            </div>
            {(profile?.plan ?? "free") === "free" ? (
              <Button asChild size="sm">
                <Link to="/upgrade">
                  <Sparkles className="mr-1.5 h-4 w-4" /> Upgrade
                </Link>
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={openPortal} disabled={!activeSub}>
                <ExternalLink className="mr-1.5 h-4 w-4" /> Manage subscription
              </Button>
            )}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Stat label="Current plan" value={planLabel(profile?.plan as PlanTier | undefined)} />
            <Stat
              label={activeSub?.cancel_at_period_end ? "Access ends" : "Renews"}
              value={
                activeSub?.current_period_end
                  ? new Date(activeSub.current_period_end).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "—"
              }
            />
            <Stat
              label="Status"
              value={activeSub ? activeSub.status.replace("_", " ") : "No active subscription"}
            />
          </div>

          {activeSub && !activeSub.cancel_at_period_end && (
            <button
              onClick={openPortal}
              className="mt-3 text-xs text-muted-foreground underline hover:text-foreground"
            >
              Cancel subscription
            </button>
          )}

          <div className="mt-6">
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Payment history
            </h3>
            <div className="mt-2 overflow-hidden rounded-lg border border-border/60">
              <table className="w-full text-xs">
                <thead className="bg-card/60 text-left text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Plan</th>
                    <th className="px-3 py-2 font-medium">Amount</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {billingLoading && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!billingLoading && invoices.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                        No invoices yet.
                      </td>
                    </tr>
                  )}
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-t border-border/60">
                      <td className="px-3 py-2 font-mono">
                        {inv.created
                          ? new Date(inv.created).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="px-3 py-2">{inv.plan ?? "—"}</td>
                      <td className="px-3 py-2 font-mono">
                        ${inv.amount_paid.toFixed(2)} {inv.currency.toUpperCase()}
                      </td>
                      <td className="px-3 py-2 capitalize">{inv.status ?? "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <FileText className="h-3 w-3" /> View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        <AccountBalanceCard />

        <DataExportCard />



        <Card className="border-border/60 bg-card/60 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Gift className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold">Refer & earn</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Share your link. When a friend signs up and subscribes to Pro, you get{" "}
                <span className="text-foreground font-semibold">1 month of Pro free</span>.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Input
              readOnly
              value={referralLink || "Generating link…"}
              className="bg-input/50 font-mono text-xs"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button onClick={copyReferral} disabled={!referralLink} variant="secondary">
              <Copy className="mr-1.5 h-4 w-4" /> Copy
            </Button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat label="Total referrals" value={(refStats?.total ?? 0).toString()} />
            <Stat label="Pending" value={(refStats?.pending ?? 0).toString()} />
            <Stat label="Paid" value={(refStats?.paid ?? 0).toString()} />
            <Stat label="Free months earned" value={(refStats?.rewardedMonths ?? 0).toString()} />
          </div>

          {refStats?.proUntil && new Date(refStats.proUntil) > new Date() && (
            <p className="mt-3 text-xs text-muted-foreground">
              Referral Pro access active until{" "}
              <span className="font-mono text-foreground">
                {new Date(refStats.proUntil).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              .
            </p>
          )}
        </Card>
      </div>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/60 bg-card/40 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-xl font-bold">{value}</div>
    </div>
  );
}

function AccountBalanceCard() {
  const stored = useAccountBalance();
  const [val, setVal] = useState<string>(stored ? String(stored) : "");

  const save = () => {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) {
      setAccountBalance(null);
      toast.success("Account balance cleared");
      return;
    }
    setAccountBalance(n);
    toast.success("Account balance saved locally");
  };

  return (
    <Card className="border-border/60 bg-card/60 p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Wallet className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold">Account balance (educational)</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Used only on this device to compute educational position size guides.
            Never sent to our servers. Never used to auto-trade.
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <div className="flex flex-1 items-center gap-2">
          <span className="text-sm text-muted-foreground">$</span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="100"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="e.g. 10000"
          />
        </div>
        <Button onClick={save} variant="secondary">
          Save
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Stored only in your browser. Clearing the field removes it.
      </p>
    </Card>
  );
}

function DataExportCard() {
  const exportFn = useServerFn(exportMyData);

  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = (await exportFn()) as { json: string };
      const blob = new Blob([res.json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `forexmind-data-export-${date}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-border/60 bg-card/60 p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold">Export my data</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Your export includes account, subscription, consent, and platform activity records
            associated with your ForexMind AI account. It does not include payment card details
            or internal security logs.
          </p>
        </div>
      </div>
      <div className="mt-4">
        <Button onClick={download} disabled={busy} variant="secondary">
          <Download className="mr-1.5 h-4 w-4" />
          {busy ? "Preparing…" : "Download JSON"}
        </Button>
      </div>
    </Card>
  );
}
